// Shared helpers for the CLUB-ADMIN meet pages (list / form / manage / console).
// Enum values mirror SLL.Api Models/Entities/Meet.cs + MeetAttendance.cs exactly —
// do not renumber. Kept separate from any member-side meet composables.
import {DateTime} from 'luxon';
import {useDates} from '~/composables/useDates';

// ── Recurrence model (module-level so the member-side useMeetFormat and the
//    Repeats field/dialog can import without a setup context) ────────────────
export const RecurrenceMode = {OneOff: 0, AutoRecurring: 1, ManualRecurring: 2};
export const RecurrenceUnit = {Week: 0, Month: 1};
export const MonthlyPattern = {DayOfMonth: 0, NthWeekday: 1, LastWeekday: 2};

// Which weekday-of-the-month the date is: 1st..5th Friday etc.
export const nthWeekdayOfMonth = (d) => Math.ceil(d.day / 7);
// True when the date is the LAST such weekday of its month.
export const isLastWeekdayOfMonth = (d) => d.plus({weeks: 1}).month !== d.month;
const ORDINAL_WORDS = ['first', 'second', 'third', 'fourth', 'fifth'];
export const nthWeekdayWord = (d) => ORDINAL_WORDS[nthWeekdayOfMonth(d) - 1];

// ── Plain-English rule builder (single shared implementation — used by
//    scheduleText, MeetRepeatsField, MeetCustomRepeatDialog and useMeetFormat).
//    `first` is a LOCAL luxon DateTime of the meet's first occurrence; the
//    weekday/ordinal always derive from it — never stored separately.
//    "Every Friday" / "Every other Friday" / "Every 3 weeks on Friday" /
//    "Monthly on day 15" / "Monthly on the third Friday" /
//    "Every 2 months on the last Friday".
export const recurrenceRuleText = (first, unit, interval, monthlyPattern) => {
    const n = parseInt(interval) || 1;
    const day = first.toFormat('cccc');
    if (unit === RecurrenceUnit.Month) {
        const on = monthlyPattern === MonthlyPattern.LastWeekday ? `the last ${day}`
            : monthlyPattern === MonthlyPattern.NthWeekday ? `the ${nthWeekdayWord(first)} ${day}`
                : `day ${first.day}`;
        return n === 1 ? `Monthly on ${on}` : `Every ${n} months on ${on}`;
    }
    if (n === 2) return `Every other ${day}`;
    return n === 1 ? `Every ${day}` : `Every ${n} weeks on ${day}`;
};

// " · until 12 Sep" / " · 6 sessions" ('' when never-ending). `until` is a
// luxon DateTime or null; `count` includes the first session (API contract).
export const recurrenceEndsSuffix = (until, count) => {
    const n = parseInt(count);
    if (!isNaN(n) && n > 0) return ` · ${n} sessions`;
    if (until && until.isValid) return ` · until ${until.toFormat('d LLL')}`;
    return '';
};

// ── firstOccursAt derivation (the When step asks for NO date on recurring
//    meets — occurrences are materialised by a background job the moment the
//    meet is created, so the first date is COMPUTED client-side) ─────────────
// Rules, from the form's display fields:
//   · OneOff / ManualRecurring — the explicitly picked `date` (ISO) at
//     `startTime`.
//   · AutoRecurring weekly    — the next date matching `weekday` whose
//     date+time is STRICTLY in the future.
//   · AutoRecurring monthly   — the next nth/last-`weekday`-of-month
//     (`monthlyOrdinal` 1–4 / LastWeekday), or the next `monthlyDay` of a
//     month for the DayOfMonth pattern (months without that day are skipped).
//   · `date`, when set on a recurring meet ("Starting later?"), is the anchor
//     FLOOR: the first matching date ≥ it.
// Returns a LOCAL luxon DateTime, or null when underspecified/invalid.
export const deriveFirstOccursAt = (item) => {
    const time = item.startTime || '19:00';
    const [hour, minute] = time.split(':').map(Number);
    // The date fields hold ISO (TapDatePicker's contract), not display text.
    const parseDate = (s) => {
        if (!s) return null;
        const d = DateTime.fromJSDate(new Date(s));
        return d.isValid ? d.startOf('day') : null;
    };
    const withTime = (d) => d.set({hour: hour || 0, minute: minute || 0, second: 0, millisecond: 0});

    if (item.recurrenceMode !== RecurrenceMode.AutoRecurring) {
        const d = parseDate(item.date);
        return d ? withTime(d) : null;
    }

    const now = DateTime.now();
    const floor = parseDate(item.date); // "Starting later?" anchor floor
    const qualifies = (d) => withTime(d) > now && (!floor || d >= floor);
    const searchStart = floor && floor > now.startOf('day') ? floor : now.startOf('day');
    const weekday = item.weekday ?? now.weekday;

    if ((item.recurrenceUnit ?? RecurrenceUnit.Week) === RecurrenceUnit.Week) {
        let d = searchStart;
        for (let i = 0; i < 8; i++, d = d.plus({days: 1})) {
            if (d.weekday === weekday && qualifies(d)) return withTime(d);
        }
        return null;
    }

    const pattern = item.monthlyPattern ?? MonthlyPattern.NthWeekday;
    let month = searchStart.startOf('month');
    for (let i = 0; i < 24; i++, month = month.plus({months: 1})) {
        let candidate = null;
        if (pattern === MonthlyPattern.DayOfMonth) {
            const day = parseInt(item.monthlyDay) || now.day;
            if (day <= month.daysInMonth) candidate = month.set({day});
        } else {
            let d = month;
            while (d.weekday !== weekday) d = d.plus({days: 1}); // first such weekday
            if (pattern === MonthlyPattern.LastWeekday) {
                while (d.plus({weeks: 1}).month === month.month) d = d.plus({weeks: 1});
                candidate = d;
            } else {
                const n = Math.min(4, Math.max(1, parseInt(item.monthlyOrdinal) || 1));
                d = d.plus({weeks: n - 1});
                if (d.month === month.month) candidate = d;
            }
        }
        if (candidate && qualifies(candidate)) return withTime(candidate);
    }
    return null;
};

// Friendly copy under "Fixtures per event". Deliberately NOT "N × 2 × T slots"
// — meets often run several fixtures concurrently and swap over the night, so a
// raw slot count means nothing. What the admin cares about is how many times
// each person plays, which is concurrency-agnostic. Keyed off max players (the
// worst case for plays-each — a full house); blank max → a plain description.
export const fixturesHint = (fixturesPerOccurrence, teamSize, maxAttendees) => {
    const n = parseInt(fixturesPerOccurrence) || 1;
    const t = parseInt(teamSize) || 1;
    const a = parseInt(maxAttendees);
    const matches = `${n} ${n === 1 ? 'match' : 'matches'} each date`;
    if (!a || a < 2) return `${matches}. Players rotate through them evenly.`;

    const slots = n * 2 * t;
    if (slots < a) return `${matches}. With ${a} players, ${a - slots} sit out each round on a rotation.`;
    const times = (x) => x === 1 ? 'once' : x === 2 ? 'twice' : `${x} times`;
    const lo = Math.floor(slots / a), hi = Math.ceil(slots / a);
    // "about" only reads right for a range; an exact figure states it plainly.
    const plays = lo === hi ? `everyone plays ${times(lo)}` : `everyone plays about ${lo} to ${hi} times`;
    return `${matches}. With ${a} players, ${plays}.`;
};

// "19:00" / "19:00–21:00" — duration is OPTIONAL (null = no fixed length).
export const meetTimesText = (startTime, durationMinutes) => {
    const time = startTime || '19:00';
    const mins = parseInt(durationMinutes);
    if (isNaN(mins) || mins <= 0) return time;
    const [h, m] = time.split(':').map(Number);
    const end = (h * 60 + m + mins) % (24 * 60);
    return `${time} to ${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
};

// The plain-English preview for the When step AND the wizard's step-3 summary:
// "Every Wednesday at 19:00 — first one Wed 22 July" /
// "One-off · Saturday 25 July, 19:00–21:00" — built on the DERIVED first date.
export const meetSchedulePreviewText = (item) => {
    const first = deriveFirstOccursAt(item);
    const times = meetTimesText(item.startTime, item.durationMinutes);
    if (item.recurrenceMode === RecurrenceMode.OneOff) {
        return first ? `One-off · ${first.toFormat('cccc d LLLL')}, ${times}` : 'One-off';
    }
    if (item.recurrenceMode === RecurrenceMode.ManualRecurring) {
        return first
            ? `Starts ${first.toFormat('cccc d LLLL')}, ${times}. Add more dates any time`
            : 'Manual dates: add each date yourself';
    }
    if (!first) return '';
    const rule = recurrenceRuleText(first, item.recurrenceUnit, item.recurrenceInterval, item.monthlyPattern);
    const until = item.recurrenceUntil ? DateTime.fromJSDate(new Date(item.recurrenceUntil)) : null;
    return `${rule} at ${times}${recurrenceEndsSuffix(until?.isValid ? until : null, item.recurrenceCount)}`
        + `, first one ${first.toFormat('ccc d LLLL')}`;
};

// The date the rule produces in a given month (or null when the month lacks it —
// day 31, or a 5th weekday). Mirrors the API materialiser; anchor derives from `first`.
const monthlyDateInMonth = (monthStart, item, first) => {
    const pattern = item.monthlyPattern ?? MonthlyPattern.NthWeekday;
    if (pattern === MonthlyPattern.DayOfMonth) {
        return first.day <= monthStart.daysInMonth ? monthStart.set({day: first.day}) : null;
    }
    let d = monthStart;
    while (d.weekday !== first.weekday) d = d.plus({days: 1}); // first such weekday
    if (pattern === MonthlyPattern.LastWeekday) {
        while (d.plus({weeks: 1}).month === monthStart.month) d = d.plus({weeks: 1});
        return d;
    }
    d = d.plus({weeks: nthWeekdayOfMonth(first) - 1});
    return d.month === monthStart.month ? d : null;
};

// The cadence the auto-recurring rule describes, from `first` — up to `limit`
// dates. Weekly steps interval×7 days; monthly steps interval months, skipping
// months that lack the pattern's date (they never yield, matching the API).
const cadenceDates = function* (item, first, limit) {
    const unit = item.recurrenceUnit ?? RecurrenceUnit.Week;
    const interval = Math.max(1, parseInt(item.recurrenceInterval) || 1);
    if (unit === RecurrenceUnit.Week) {
        let d = first;
        for (let i = 0; i < limit; i++, d = d.plus({days: 7 * interval})) yield d;
        return;
    }
    const firstMonth = first.startOf('month');
    let yielded = 0;
    for (let k = 0; yielded < limit && k < limit * 3 + 24; k++) {
        const date = monthlyDateInMonth(firstMonth.plus({months: interval * k}), item, first);
        if (date) {
            yield date.set({hour: first.hour, minute: first.minute, second: 0, millisecond: 0});
            yielded++;
        }
    }
};

// Live "how many sessions this creates" line for the When preview — the single
// cheapest error-prevention win for a PAID format (benchmark verdict, 2026-07-20):
//   after-N → "Creates 6 sessions, last on Fri 25 Sep"
//   on-date → "Creates 12 sessions through 30 Sep"
//   never / non-recurring → '' (nothing to count).
export const meetSeriesCountText = (item) => {
    if (item.recurrenceMode !== RecurrenceMode.AutoRecurring) return '';
    const first = deriveFirstOccursAt(item);
    if (!first) return '';
    const plural = (n) => `${n} session${n === 1 ? '' : 's'}`;

    const count = parseInt(item.recurrenceCount);
    if (!isNaN(count) && count > 0) {
        const dates = [...cadenceDates(item, first, count)];
        const last = dates[dates.length - 1];
        return `Creates ${plural(count)}${last ? `, last on ${last.toFormat('ccc d LLL')}` : ''}`;
    }

    const until = item.recurrenceUntil
        ? DateTime.fromJSDate(new Date(item.recurrenceUntil)).endOf('day') : null;
    if (!until || !until.isValid) return ''; // never-ending — no finite count
    let n = 0;
    for (const d of cadenceDates(item, first, 520)) {
        if (d > until) break;
        n++;
    }
    return n > 0 ? `Creates ${plural(n)} through ${until.toFormat('d LLL')}` : '';
};

// Platform payment-terms template (incl. the under-18 clause and the refill-waiver
// sentence) — seeded for paid meets by both the create wizard and the settings
// form's "Reset to template".
// Deliberately fee-free: the amount lives on the RSVP/booking screens, so the
// terms never go stale (and members aren't re-asked to accept) when the fee changes.
export const meetTermsTemplate = ({name, clubName}) => `${name || 'This meet'} payment terms

1. The session fee shown when you RSVP is charged to your saved card after each session you attend.
2. Cancellation fees follow the published cancellation policy. If your spot is taken by someone else after you cancel, the cancellation fee is waived.
3. Payments are processed by Stripe on behalf of ${clubName || 'the club'}, which appears on your statement and issues any refunds.
4. Refunds are at the club's discretion. Contact the club directly.
5. If the club cancels a session you are not charged; anything already billed is refunded in full.
6. If you are under 18, check with a parent or guardian before paying.`;

// ── Admin truth-telling helpers (ADMIN_UX_AUDIT A13 / B2 / N15) — pure, unit
//    tested in tests/unit/meet-admin-copy.test.ts ─────────────────────────────

// "3 spots left" / "Full" for ONE date: the date's own effective cap (the API's
// maxAttendees = override ?? meet default) wins over the meet's default, so a
// date whose cap was raised stops reading "Full". Null = unlimited → no text.
export const occurrenceSpotsLeftText = (occurrence, meet) => {
    const cap = occurrence?.maxAttendees !== undefined
        ? occurrence.maxAttendees
        : (meet?.maxAttendees ?? null);
    if (cap == null) return null;
    const left = cap - (occurrence?.attendingCount ?? 0);
    if (left > 0) return `${left} spot${left === 1 ? '' : 's'} left`;
    return left === 0 ? 'Full' : `Over capacity by ${-left}`;
};

// What deleting a meet takes with it, from the API's upcomingDateCount /
// upcomingRsvpCount (missing on older APIs → the generic but honest sentence).
export const meetDeleteImpactText = (meet) => {
    const dates = meet?.upcomingDateCount;
    const rsvps = meet?.upcomingRsvpCount;
    if (dates == null) {
        return 'Its upcoming dates and everyone\'s RSVPs for them are removed, everyone affected is told the dates are cancelled, and members can no longer RSVP.';
    }
    if (dates === 0) return 'It has no upcoming dates, so no RSVPs are affected.';
    const d = `${dates} upcoming date${dates === 1 ? '' : 's'}`;
    const r = rsvps == null ? 'their RSVPs'
        : rsvps === 0 ? 'no RSVPs' : `${rsvps} RSVP${rsvps === 1 ? '' : 's'}`;
    return `${d} with ${r} will be removed. Everyone affected is told the dates are cancelled, and members can no longer RSVP.`;
};

// Totals for the Review charges panel. `rows` = [{attendance, preview}] (the
// panel's chargeRows); `hasPayer(attendance)` says whether anyone can be billed
// for the row (a linked account or a "Bill to" assignment) — rows without one
// settle offline and never reach Stripe, so they stay out of Gross and are
// counted separately (N15). `payerOf(attendance)` names who is billed: each
// payer's rows are ONE charge (a parent billed for two children pays once), so
// `bundles` holds one amount per charge. What those charges split into comes
// from the server (useFeeEstimate), never from a sum here (Payments plan v3).
export const meetChargeTotals = (rows, {hasPayer = () => true, payerOf = (a) => a.playerUserId ?? a.playerId} = {}) => {
    let gross = 0;
    let offlineCount = 0;
    let offlineMinor = 0;
    const byPayer = new Map();
    for (const row of rows ?? []) {
        const a = row.attendance;
        if (!row.preview || a.waived) continue;
        if (a.status === 7 /* AttendanceStatus.CancelledRefilled */) continue;
        if (row.preview.amountMinor <= 0) continue;
        if (!hasPayer(a)) {
            offlineCount++;
            offlineMinor += row.preview.amountMinor;
            continue;
        }
        gross += row.preview.amountMinor;
        const payer = payerOf(a);
        byPayer.set(payer, (byPayer.get(payer) ?? 0) + row.preview.amountMinor);
    }
    const bundles = [...byPayer.values()];
    return {gross, chargeCount: bundles.length, bundles, offlineCount, offlineMinor};
};

// One sentence for the Charge now confirm: "Charges 9 people, £72.00 total
// (you receive about £64.10)." plus the offline count when there is one.
export const chargeNowSummaryText = (totals, money) => {
    if (!totals) return null;
    const people = `${totals.chargeCount} ${totals.chargeCount === 1 ? 'person' : 'people'}`;
    let text = totals.chargeCount
        ? `Charges ${people}, ${money(totals.gross)} total`
        + (totals.net != null ? ` (you receive about ${money(totals.net)})` : '') + '.'
        : 'Nobody is charged by card.';
    if (totals.offlineCount) {
        text += ` ${totals.offlineCount} ${totals.offlineCount === 1 ? 'person has' : 'people have'} no account to bill`
            + ` (${money(totals.offlineMinor)}). Collect that offline.`;
    }
    return text;
};

export function useMeetAdmin() {
    // Captured at composable creation (setup context) — the helpers below run
    // inside loader callbacks where useNuxtApp() is no longer available.
    const {$luxon} = useNuxtApp();
    const {startOfDayIso} = useDates();

    // ── Enums (SLL.Api) — RecurrenceMode is module-level above ───────────────
    const UnderMinimumPolicy = {AlertAdmin: 0, AutoCancel: 1};
    const GenerationMode = {Manual: 0, RandomShuffle: 1, SkillBalanced: 2};
    const GenerationTrigger = {Manual: 0, AtRsvpClose: 1, WhenFull: 2};
    const BillingFrequency = {PerOccurrence: 0, Weekly: 1, Monthly: 2};
    const OccurrenceStatus = {Scheduled: 0, Cancelled: 1, Completed: 2, Finalised: 3};
    const AttendanceStatus = {
        Attending: 0, Declined: 1, Waitlisted: 2, PromotionOffered: 3, CancelledFree: 4,
        CancelledPendingCharge: 5, CancelledCharged: 6, CancelledRefilled: 7, NoShow: 8, Attended: 9
    };

    // ── RSVP window options — shared by the create wizard and the settings form
    //    so the two surfaces can never drift apart. "A set number of days" leads
    //    because it's the default and the most common choice. ───────────────────
    const rsvpOpenModeOptions = [
        {value: 1, title: 'A set number of days before each date'},
        {value: 0, title: 'Immediately, as soon as the date is published'},
        {value: 2, title: 'At a random moment before (first come, first served)'},
    ];
    const rsvpOpenDaysOptions = [
        {value: 1, title: '1 day before'},
        {value: 2, title: '2 days before'},
        {value: 3, title: '3 days before'},
        {value: 5, title: '5 days before'},
        {value: 7, title: '7 days before'},
        {value: 10, title: '10 days before'},
        {value: 14, title: '2 weeks before'},
        {value: 21, title: '3 weeks before'},
        {value: 28, title: '4 weeks before'},
    ];
    const rsvpCloseOptions = [
        {value: 0, title: 'At start time'},
        {value: 60, title: '1 hour before start'},
        {value: 120, title: '2 hours before start'},
        {value: 360, title: '6 hours before start'},
        {value: 720, title: '12 hours before start'},
        {value: 1440, title: '1 day before start'},
        {value: 2880, title: '2 days before start'},
    ];

    // ── Plain-English schedule ("Every Wednesday 19:00", "One-off · Sat 19 Jul",
    //    "Monthly on the last Friday 19:00 · 6 sessions") ───────────────────────
    const luxonFromDb = (dateStr) => $luxon.fromISO(dateStr, {zone: 'utc'}).toLocal();
    const luxonNow = () => $luxon.now();

    const scheduleText = (meet) => {
        if (!meet) return '';
        const first = meet.firstOccursAt ? luxonFromDb(meet.firstOccursAt) : null;
        if (meet.recurrenceMode === RecurrenceMode.OneOff) {
            return first ? `One-off · ${first.toFormat('ccc d LLL')}` : 'One-off';
        }
        if (meet.recurrenceMode === RecurrenceMode.ManualRecurring) {
            return 'Manual dates';
        }
        if (!first) return 'Recurring';
        const rule = recurrenceRuleText(first, meet.recurrenceUnit, meet.recurrenceInterval, meet.monthlyPattern);
        const until = meet.recurrenceUntil ? luxonFromDb(meet.recurrenceUntil) : null;
        return `${rule} ${first.toFormat('HH:mm')}${recurrenceEndsSuffix(until, meet.recurrenceCount)}`;
    };

    // ── Money ────────────────────────────────────────────────────────────────
    const currencySymbols = {gbp: '£', eur: '€', usd: '$', aud: 'A$', nzd: 'NZ$', cad: 'C$', zar: 'R'};
    const currencySymbol = (currency) => currencySymbols[(currency || 'gbp').toLowerCase()] ?? ((currency || '').toUpperCase() + ' ');

    const formatMoneyMinor = (minor, currency) => {
        if (minor == null) return '';
        return currencySymbol(currency) + (minor / 100).toFixed(2);
    };

    // Always "per session": the fee is per attended session regardless of how
    // often it's billed — "£3.00 · monthly bill" read as £3/month, which it isn't.
    const feeText = (meet) => {
        if (!meet || meet.feeMinor == null) return 'Free';
        const base = `${formatMoneyMinor(meet.feeMinor, meet.currency)} per session`;
        if (meet.billingFrequency === BillingFrequency.Weekly) return `${base} · billed weekly`;
        if (meet.billingFrequency === BillingFrequency.Monthly) return `${base} · billed monthly`;
        return base;
    };

    // Client-side echo of the API's PeriodBoundaryAfter: the meet's configured
    // billing moment (Europe/London, like the server) strictly after a session
    // end. PerOccurrence bills at the session itself.
    const nextBillingRunAfter = (meet, sessionEndLuxon) => {
        if (meet.billingFrequency === BillingFrequency.PerOccurrence) return sessionEndLuxon;
        const local = sessionEndLuxon.setZone('Europe/London');
        const minutes = meet.billingTimeMinutes ?? 0;
        let candidate;
        if (meet.billingFrequency === BillingFrequency.Weekly) {
            // DayOfWeek is .NET-style (Sunday=0); luxon weekday is Monday=1..Sunday=7.
            const target = ((meet.billingDayOfWeek ?? 1) + 6) % 7 + 1;
            candidate = local.startOf('day').plus({days: (target - local.weekday + 7) % 7, minutes});
            if (candidate <= local) candidate = candidate.plus({days: 7});
        } else {
            const dayFor = (dt) => Math.min(meet.billingDayOfMonth ?? 1, dt.daysInMonth);
            candidate = local.startOf('month').plus({days: dayFor(local) - 1, minutes});
            if (candidate <= local) {
                const next = local.startOf('month').plus({months: 1});
                candidate = next.plus({days: dayFor(next) - 1, minutes});
            }
        }
        return candidate;
    };

    // Payout estimates are worked out by the server (usePaymentEstimate.js, Payments
    // plan v3 phase 2): the website carries no fee or commission sums. This caption
    // still describes the Stripe estimate a server figure was made with.
    const stripeFeeCaption = (profile) => profile
        ? [profile.assumption, profile.conversionNote,
            'The exact fee for each payment appears on your payments dashboard once it settles.']
            .filter(Boolean).join(' ')
        : null;

    // ── Register / charge-preview helpers (console) ──────────────────────────
    const attendanceStatusLabel = (status) => {
        switch (status) {
            case AttendanceStatus.Attending: return 'Attending';
            case AttendanceStatus.Declined: return 'Declined';
            case AttendanceStatus.Waitlisted: return 'Waitlisted';
            case AttendanceStatus.PromotionOffered: return 'Promotion offered';
            case AttendanceStatus.CancelledFree: return 'Cancelled · free';
            case AttendanceStatus.CancelledPendingCharge: return 'Late cancel · charge pending';
            case AttendanceStatus.CancelledCharged: return 'Late cancel · charged';
            case AttendanceStatus.CancelledRefilled: return 'Late cancel · spot refilled';
            case AttendanceStatus.NoShow: return 'No-show';
            case AttendanceStatus.Attended: return 'Attended';
            default: return '';
        }
    };

    const occurrenceStatusLabel = (status) => {
        switch (status) {
            case OccurrenceStatus.Scheduled: return 'Scheduled';
            case OccurrenceStatus.Cancelled: return 'Cancelled';
            case OccurrenceStatus.Completed: return 'Completed';
            case OccurrenceStatus.Finalised: return 'Finalised';
            default: return '';
        }
    };

    // Charge preview per attendance row (mirrors MeetFinalisationService intent):
    // Attending/Attended/NoShow → 100%; CancelledPendingCharge → tier % (provisional);
    // CancelledRefilled/CancelledFree → nothing; waived rows are struck.
    const chargePreview = (attendance, feeMinor) => {
        if (feeMinor == null) return null;
        const s = attendance.status;
        let percent = null;
        if (s === AttendanceStatus.Attending || s === AttendanceStatus.Attended || s === AttendanceStatus.NoShow) {
            percent = 100;
        } else if (s === AttendanceStatus.CancelledPendingCharge || s === AttendanceStatus.CancelledCharged) {
            percent = attendance.cancellationChargePercent ?? 0;
        } else if (s === AttendanceStatus.CancelledRefilled) {
            percent = 0;
        } else {
            return null; // declined / waitlisted / free-cancel rows never charge
        }
        return {percent, amountMinor: Math.round(feeMinor * percent / 100)};
    };

    // ── Form <-> API transforms (shared by the list page and the manage page's
    //    edit dialog). The form edits weekday/date/startTime/duration/fee display
    //    fields; the API stores firstOccursAt/durationMinutes/feeMinor —
    //    firstOccursAt is DERIVED (deriveFirstOccursAt) unless explicitly picked. ─
    const meetInitialItem = (clubId) => {
        const now = $luxon.now();
        return {
            clubId,
            name: '',
            isPrivate: false,
            informationHtml: null,
            recurrenceMode: RecurrenceMode.AutoRecurring,
            date: null, // one-off/manual date, or the recurring "Starting later?" floor
            weekday: now.weekday, // defaults to today's weekday
            monthlyOrdinal: Math.min(4, nthWeekdayOfMonth(now)),
            monthlyDay: now.day,
            startTime: '19:00',
            durationMinutes: null, // null = no fixed length
            recurrenceUnit: RecurrenceUnit.Week,
            recurrenceInterval: 1,
            monthlyPattern: null,
            recurrenceUntil: null,
            recurrenceCount: null,
            minAttendees: null,
            maxAttendees: null,
            waitlistEnabled: true,
            rsvpCloseMinutesBeforeStart: 60, // an hour before start
            rsvpOpenMode: 1, // DaysBefore
            rsvpOpenDaysBefore: 7,
            rsvpOpenNotify: true,
            underMinimumPolicy: UnderMinimumPolicy.AlertAdmin,
            requiresCheckIn: false, // register (check-in on the day) is opt-in
            recordFixtures: true,
            generationMode: GenerationMode.SkillBalanced, // matches the API default: pair by skill, results win
            fixturesPerOccurrence: 1,
            roundsPerOccurrence: 1, // the control; matches follow when a capacity is set
            generationTrigger: GenerationTrigger.AtRsvpClose, // matches the API default: pair up when RSVPs close
            teamSize: 1,
            avoidRecentRepeats: true,
            fixtureAiAnalysis: true,
            fee: null,
            currency: null,
            billingFrequency: BillingFrequency.PerOccurrence,
            billingDayOfWeek: 1, // Monday
            billingDayOfMonth: 1,
            billingTimeMinutes: 9 * 60,
            graceWindowHours: 24,
            termsHtml: null,
            cancellationTiers: [],
        };
    };

    // API response -> form display fields (weekday/ordinal/day reverse-mapped
    // from firstOccursAt — never stored separately).
    const meetItemTransform = (meet) => {
        const item = {...meet};
        // TapDatePicker holds an INSTANT, not a calendar-day string: "2026-08-18"
        // parses as UTC midnight and renders as the day before west of Greenwich.
        item.recurrenceUntil = meet.recurrenceUntil
            ? startOfDayIso(luxonFromDb(meet.recurrenceUntil).toJSDate()) : null;
        // Sensible editing defaults for non-auto meets (beforeSave nulls them again).
        item.recurrenceUnit = meet.recurrenceUnit ?? RecurrenceUnit.Week;
        item.recurrenceInterval = meet.recurrenceInterval ?? 1;
        item.monthlyPattern = meet.monthlyPattern ?? null;
        item.recurrenceCount = meet.recurrenceCount ?? null;
        item.durationMinutes = meet.durationMinutes ?? null;
        if (meet.firstOccursAt) {
            const start = luxonFromDb(meet.firstOccursAt);
            item.startTime = start.toFormat('HH:mm');
            item.weekday = start.weekday;
            item.monthlyOrdinal = Math.min(4, nthWeekdayOfMonth(start));
            item.monthlyDay = start.day;
            if (meet.recurrenceMode === RecurrenceMode.AutoRecurring) {
                // Recurring meets carry no date — except when a FUTURE firstOccursAt
                // isn't the next natural occurrence: that's a "Starting later?" date.
                item.date = null;
                if (start > $luxon.now()) {
                    const natural = deriveFirstOccursAt({...item, date: null});
                    if (!natural || !natural.hasSame(start, 'day')) item.date = startOfDayIso(start.toJSDate());
                }
            } else {
                item.date = startOfDayIso(start.toJSDate());
            }
        }
        item.fee = meet.feeMinor != null ? (meet.feeMinor / 100).toFixed(2) : null;
        item.cancellationTiers = (meet.cancellationTiers || [])
            .map(t => ({hoursBefore: t.hoursBefore, chargePercent: t.chargePercent}));
        return item;
    };

    // Form display fields -> API request body (MeetRequest).
    const meetBeforeSave = (item) => {
        const body = {...item};

        // firstOccursAt: derived (recurring) or explicit (one-off/manual/floor) —
        // deriveFirstOccursAt handles all modes. Local -> UTC ISO (inputDateForDb canon).
        const first = deriveFirstOccursAt(item);
        body.firstOccursAt = first ? first.toUTC().toISO() : null;
        const duration = parseInt(item.durationMinutes);
        body.durationMinutes = !isNaN(duration) && duration > 0 ? duration : null;

        if (item.recurrenceMode === RecurrenceMode.AutoRecurring) {
            body.recurrenceUnit = item.recurrenceUnit ?? RecurrenceUnit.Week;
            body.recurrenceInterval = parseInt(item.recurrenceInterval) || 1;
            body.monthlyPattern = body.recurrenceUnit === RecurrenceUnit.Month
                ? (item.monthlyPattern ?? MonthlyPattern.DayOfMonth)
                : null;
            // Ends: recurrenceUntil XOR recurrenceCount (count wins when both linger
            // from UI toggling — the Repeats control keeps them exclusive anyway).
            const count = parseInt(item.recurrenceCount);
            if (!isNaN(count) && count > 0) {
                body.recurrenceCount = count;
                body.recurrenceUntil = null;
            } else {
                body.recurrenceCount = null;
                body.recurrenceUntil = item.recurrenceUntil || null;
            }
        } else {
            body.recurrenceUnit = null;
            // interval is a non-nullable int on the API — null breaks binding
            body.recurrenceInterval = 1;
            body.monthlyPattern = null;
            body.recurrenceUntil = null;
            body.recurrenceCount = null;
        }

        body.minAttendees = item.minAttendees === '' || item.minAttendees == null ? null : parseInt(item.minAttendees);
        body.maxAttendees = item.maxAttendees === '' || item.maxAttendees == null ? null : parseInt(item.maxAttendees);
        body.fixturesPerOccurrence = parseInt(item.fixturesPerOccurrence) || 1;
        body.roundsPerOccurrence = parseInt(item.roundsPerOccurrence) || 1; // the API derives fixtures from this when a capacity is set

        const fee = parseFloat(item.fee);
        body.feeMinor = !isNaN(fee) && fee > 0 ? Math.round(fee * 100) : null;
        body.currency = body.feeMinor != null ? (item.currency || null) : null;
        // Full replacement semantics: [] clears the policy; paid meets send the rows.
        body.cancellationTiers = body.feeMinor == null ? [] : (item.cancellationTiers || [])
            .map(t => ({hoursBefore: parseInt(t.hoursBefore) || 0, chargePercent: parseInt(t.chargePercent) || 0}));
        if (body.feeMinor == null) {
            body.termsHtml = null;
        }

        // Display-only fields the API doesn't know.
        delete body.date;
        delete body.startTime;
        delete body.weekday;
        delete body.monthlyOrdinal;
        delete body.monthlyDay;
        delete body.fee;
        // Detail-response baggage on edits.
        delete body.players;
        delete body.occurrences;
        delete body.sport;
        delete body.scoringSystem;
        delete body.club;
        delete body.visibleInviteLink;

        return body;
    };

    return {
        RecurrenceMode, RecurrenceUnit, MonthlyPattern, UnderMinimumPolicy, GenerationMode,
        GenerationTrigger, BillingFrequency, OccurrenceStatus, AttendanceStatus,
        rsvpOpenModeOptions, rsvpOpenDaysOptions, rsvpCloseOptions,
        scheduleText, feeText, nextBillingRunAfter, formatMoneyMinor, currencySymbol, stripeFeeCaption,
        attendanceStatusLabel, occurrenceStatusLabel, chargePreview,
        luxonFromDb,
        meetInitialItem, meetItemTransform, meetBeforeSave,
    };
}
